import { formatTimestamp, type KeyMoment, type RecapTemplate } from "~/meetingRecorder/utils";
import toasty from "~/ui/toast";

import type { TranscriptModal_meetingRecording$data as MeetingRecordingType } from "~/__generated__/TranscriptModal_meetingRecording.graphql";

export type RecapSection =
	| "Summary"
	| "ActionItems"
	| "Decisions"
	| "Chapters"
	| "SingleChapter"
	| "SingleActionItem"
	| "All"
	| "Custom";

type Chapter = NonNullable<NonNullable<MeetingRecordingType["activeSummary"]>["chapters"]>[0];
type ActionItems = NonNullable<MeetingRecordingType["activeSummary"]>["actionItems"];
type Decisions = NonNullable<MeetingRecordingType["activeSummary"]>["decisions"];
type CleanedMeetingRecordingType = Omit<MeetingRecordingType, " $fragmentType">;

export type CopyRecapOptions = {
	customSectionName?: string;
	singleChapterTimestamp?: number;
	singleChapterTitle?: string;
	singleActionItemTimestamp?: number;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let noHeadingRenderer: any;

const renderMd = async (md: string, inline = false) => {
	const { marked } = await import("marked");
	if (!noHeadingRenderer) {
		noHeadingRenderer = new marked.Renderer();
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		noHeadingRenderer.heading = function ({ tokens, raw, text }: { tokens: any; raw: string; text: string }) {
			return this.paragraph({ tokens, type: "paragraph", raw, text });
		};
	}
	if (inline) {
		return marked.parseInline(md, { renderer: noHeadingRenderer });
	}
	return marked.parse(md, { renderer: noHeadingRenderer });
};
const stripMd = (md: string) => {
	// Remove all markdown formatting
	let output = md;

	// Remove headers at the beginning of lines
	output = output.replace(/^#+ /gm, "");

	// Remove bold, em, italic, underline, strikethrough
	output = output.replace(/\*\*(.*?)\*\*/g, "$1");
	output = output.replace(/\*(.*?)\*/g, "$1");
	output = output.replace(/__(.*?)__/g, "$1");
	output = output.replace(/~~(.*?)~~/g, "$1");

	return output;
};

export async function copyRecapSection(
	meetingRecording: CleanedMeetingRecordingType,
	sectionToCopy: RecapSection,
	options?: CopyRecapOptions,
) {
	const activeSummary = meetingRecording.activeSummary;
	if (!activeSummary) return;

	let entityName = "Entire Recap";
	let [html, text] = getMeetingHeaderForCopying(meetingRecording.title, meetingRecording.url || null);

	if (
		sectionToCopy === "All" &&
		activeSummary.activeRecapTemplate &&
		activeSummary.templatedRecaps[activeSummary.activeRecapTemplate]
	) {
		const [customRecapHtml, customRecapText] = await formatCustomTemplate(
			activeSummary.templatedRecaps[activeSummary.activeRecapTemplate] as RecapTemplate,
			meetingRecording.url || null,
			meetingRecording.meetingRecordedVideoDuration,
		);

		html += customRecapHtml;
		text += customRecapText;

		if (activeSummary.chapters && activeSummary.chapters.length > 0) {
			for (const chapter of activeSummary.chapters) {
				const [chapterHtml, chapterText] = await formatChapter(
					chapter,
					meetingRecording.url || null,
					meetingRecording.meetingRecordedVideoDuration,
				);
				html += chapterHtml;
				text += chapterText;
			}
		}

		return await copyToClipboard(html, text, entityName);
	}

	switch (sectionToCopy) {
		case "Summary":
		case "Decisions":
		case "Chapters":
			entityName = sectionToCopy;
			break;
		case "SingleChapter":
			entityName = "Chapter";
			break;
		case "ActionItems":
			entityName = "Action Items";
			break;
		case "SingleActionItem":
			entityName = "Action item";
			break;
		case "Custom":
			entityName = options?.customSectionName || "";
			break;
	}

	if (sectionToCopy === "Summary" || sectionToCopy === "All") {
		html += "<h2><b>Summary</b></h2>";
		text += "Summary\n";

		html += "";
		if (activeSummary.summary) {
			html += await renderMd(`${activeSummary.summary}`);
			text += `${stripMd(activeSummary.summary)}`;
		} else {
			html += `<i>No summary for this meeting.</i>`;
			text += "No summary for this meeting.";
		}
		html += "";
		text += "\n\n";
	}

	if (sectionToCopy === "ActionItems" || sectionToCopy === "All") {
		const [aiHtml, aiText] = await formatActionItems(
			activeSummary.actionItems,
			meetingRecording.url || null,
			meetingRecording.meetingRecordedVideoDuration,
		);

		html += aiHtml;
		text += aiText;
	}

	if (sectionToCopy === "Decisions" || sectionToCopy === "All") {
		const [decisionsHtml, decisionsText] = await formatDecisions(
			activeSummary.decisions,
			meetingRecording.url || null,
			meetingRecording.meetingRecordedVideoDuration,
		);

		html += decisionsHtml;
		text += decisionsText;
	}

	if (
		sectionToCopy === "SingleChapter" &&
		options &&
		(options.singleChapterTimestamp !== undefined || options.singleChapterTitle !== undefined) &&
		activeSummary.chapters
	) {
		let chapter: Chapter | null = null;
		if (options.singleChapterTimestamp) {
			chapter = activeSummary.chapters.find(c => c.timestamp === options.singleChapterTimestamp) || null;
		} else if (options.singleChapterTitle) {
			chapter = activeSummary.chapters.find(c => c.title === options.singleChapterTitle) || null;
		}

		if (chapter) {
			const [chapterHtml, chapterText] = await formatChapter(
				chapter,
				meetingRecording.url || null,
				meetingRecording.meetingRecordedVideoDuration,
			);
			html += chapterHtml;
			text += chapterText;
		}
	}

	if (
		sectionToCopy === "SingleActionItem" &&
		options &&
		options.singleActionItemTimestamp !== undefined &&
		activeSummary.actionItems
	) {
		const actionItem = activeSummary.actionItems.find(ai => ai.timestamp === options.singleActionItemTimestamp);
		if (actionItem) {
			const [aiHtml, aiText] = await formatActionItems(
				[actionItem],
				meetingRecording.url || null,
				meetingRecording.meetingRecordedVideoDuration,
			);

			html += aiHtml;
			text += aiText;
		}
	}

	if (
		(sectionToCopy === "Chapters" || sectionToCopy === "All") &&
		activeSummary.chapters &&
		activeSummary.chapters.length > 0
	) {
		// Check if any chapters have timestamps
		const hasTimestamps = activeSummary.chapters.some(chapter => chapter.timestamp);
		if (hasTimestamps) {
			html += "<h2><b>Chapters</b></h2>";
			text += "Chapters\n";
		} else {
			html += "<h2><b>Topics</b></h2>";
			text += "Topics\n";
		}

		for (const chapter of activeSummary.chapters) {
			const [chapterHtml, chapterText] = await formatChapter(
				chapter,
				meetingRecording.url || null,
				meetingRecording.meetingRecordedVideoDuration,
			);
			html += chapterHtml;
			text += chapterText;
		}
	}

	if (
		sectionToCopy === "Custom" &&
		options?.customSectionName &&
		activeSummary.activeRecapTemplate &&
		activeSummary.templatedRecaps[activeSummary.activeRecapTemplate]
	) {
		const templateData = activeSummary.templatedRecaps[activeSummary.activeRecapTemplate] as RecapTemplate;
		const sectionData = templateData[options.customSectionName];

		const [sectionHtml, sectionText] = await formatCustomSection(
			options.customSectionName,
			sectionData,
			meetingRecording.url || null,
			meetingRecording.meetingRecordedVideoDuration,
		);

		html += sectionHtml;
		text += sectionText;
	}

	await copyToClipboard(html, text, entityName);
}

async function copyToClipboard(html: string, text: string, entityName: string) {
	if (typeof ClipboardItem !== "undefined") {
		const clipboardItem = new ClipboardItem({
			"text/html": new Blob([html], { type: "text/html" }),
			"text/plain": new Blob([text], { type: "text/plain" }),
		});
		await navigator.clipboard.write([clipboardItem]);
	} else {
		await navigator.clipboard.writeText(text);
	}

	toasty.success(`${entityName} copied to clipboard!`);
}

async function formatChapter(chapter: Chapter, baseUrl: string | null, videoDuration: number | null) {
	const timeLabel = chapter.timestamp ? formatTimestamp(chapter.timestamp, videoDuration) : "";
	const timestampLink =
		baseUrl && chapter.timestamp ? `<a href="${baseUrl}&ts=${chapter.timestamp}">${timeLabel}</a>` : timeLabel;

	// Include timestamp link only if there's an actual timestamp
	let html = `<p>${timestampLink ? `[${timestampLink}] ` : ""}<b>${chapter.title}</b></p>`;
	let text = `${timeLabel ? `[${timeLabel}] ` : ""}${chapter.title}\n`;

	if (chapter.bulletPoints.length > 0) {
		html += "<ul>";
	}

	for (const point of chapter.bulletPoints) {
		// Use textShort if available, otherwise use text
		const pointText = point.textShort && point.textShort.trim() !== "" ? point.textShort : point.text;
		const pointHtml = await renderMd(pointText, true);
		if (point.timestamp) {
			const timestampLink = baseUrl
				? `<a href="${baseUrl}&ts=${point.timestamp}">${formatTimestamp(point.timestamp, videoDuration)}</a>`
				: formatTimestamp(point.timestamp, videoDuration);
			html += `<li>${pointHtml} ${timestampLink}</li>`;
			text += `- ${stripMd(pointText)} ${timestampLink}\n`;
		} else {
			html += `<li>${pointHtml}</li>`;
			text += `- ${stripMd(pointText)}\n`;
		}
	}

	if (chapter.bulletPoints.length > 0) {
		html += "</ul>";
	}

	html += "</p>";
	text += "\n";

	return [html, text];
}

async function formatActionItems(actionItems: ActionItems, baseUrl: string | null, videoDuration: number | null) {
	let html = "<h2><b>Action items</b></h2>";
	let text = "Action items\n";

	if (!actionItems || actionItems.length === 0) {
		html += "<p><i>No action items detected.</i></p>";
		text += "No action items detected\n\n";

		return [html, text];
	}

	html += "<ul>";

	for (const ai of actionItems) {
		html += `<li data-fellow-serialized-list="true">`;
		text += "- ";

		const timeLabel = formatTimestamp(ai.timestamp, videoDuration);
		const timestampLink = baseUrl ? `<a href="${baseUrl}&ts=${ai.timestamp}">${timeLabel}</a>` : timeLabel;

		const div = document.createElement("div");
		div.setAttribute("data-block-id", ai.timestamp.toString());
		div.setAttribute("data-block-type", "todo");
		div.setAttribute("data-done", "false");
		div.setAttribute("data-wont-do", "false");
		div.setAttribute("data-placeholder", "");
		div.setAttribute("data-linked-issue", "null");
		div.setAttribute("data-status-condition", "SINGLE");

		let assigneePart = "";
		if (ai.assignees && ai.assignees.length > 0) {
			div.setAttribute("data-assignees", ai.assignees.map(assignee => `${assignee.userId}:not_done`).join(","));
			div.setAttribute("data-creator", ai.assignees[0].userId ?? "");

			assigneePart = ai.assignees.map(assignee => `${assignee.fullName}`).join(", ");
		} else {
			div.setAttribute("data-assignees", "");
		}

		const aiHtml = await renderMd(ai.text, true);

		div.innerHTML = `${assigneePart}: ${aiHtml} ${timestampLink}`;
		html += div.outerHTML;
		html += "</li>";

		text += `${assigneePart}: ${stripMd(ai.text)} ${timeLabel}\n`;
	}

	html += "</ul>";
	text += "\n";

	return [html, text];
}

async function formatDecisions(decisions: Decisions, baseUrl: string | null, videoDuration: number | null) {
	let html = "<h2><b>Decisions</b></h2>";
	let text = "Decisions\n";

	if (!decisions || decisions.length === 0) {
		html += "<p><i>No decisions detected.</i></p>";
		text += "No decisions detected\n\n";

		return [html, text];
	}

	html += "<ul>";

	for (const decision of decisions) {
		html += "<li>";
		text += "- ";

		const decisionHtml = await renderMd(decision.text, true);

		const timeLabel = formatTimestamp(decision.timestamp, videoDuration);
		const timestampLink = baseUrl ? `<a href="${baseUrl}&ts=${decision.timestamp}">${timeLabel}</a>` : timeLabel;

		html += `${decisionHtml} ${timestampLink}</li>`;
		text += `${stripMd(decision.text)} ${timeLabel}\n`;
	}

	html += "</ul>";
	text += "\n";

	return [html, text];
}

async function formatCustomTemplate(template: RecapTemplate, baseUrl: string | null, videoDuration: number | null) {
	let html = "";
	let text = "";

	for (const [sectionName, keyMoments] of Object.entries(template)) {
		const [sectionHtml, sectionText] = await formatCustomSection(sectionName, keyMoments, baseUrl, videoDuration);

		html += sectionHtml;
		text += sectionText;
	}

	return [html, text];
}

async function formatCustomSection(
	sectionName: string,
	keyMoments: KeyMoment[],
	baseUrl: string | null,
	videoDuration: number | null,
) {
	let html = `<h2><b>${sectionName}</b></h2>`;
	let text = `${sectionName}\n`;

	if (keyMoments.length === 0) {
		html += "<p><i>No key points detected.</i></p>";
		text += "No key points detected\n\n";

		return [html, text];
	}

	html += "<ul>";

	for (const keyMoment of keyMoments) {
		html += "<li>";
		text += "- ";

		const keyMomentHtml = await renderMd(keyMoment.text, true);

		const timeLabel = formatTimestamp(keyMoment.timestamp, videoDuration);
		const timestampLink = baseUrl ? `<a href="${baseUrl}&ts=${keyMoment.timestamp}">${timeLabel}</a>` : timeLabel;

		html += `${keyMomentHtml} ${timestampLink}</li>`;
		text += `${stripMd(keyMoment.text)} ${timeLabel}\n`;
	}

	if (keyMoments.length > 0) {
		html += "</ul>";
	}

	text += "\n";

	return [html, text];
}

const AI_FEATURE_URL =
	// eslint-disable-next-line max-len
	"https://fellow.app/features/ai?utm_source=fellow_core&utm_medium=web&utm_campaign=meeting_recorder&utm_content=copy_AI_note";

function getMeetingHeaderForCopying(title: string, url: string | null) {
	const titleWithLink = url ? `<a href="${url}">${title}</a>` : title;
	const html = `<p><b>Meeting: ${titleWithLink}</b> | AI Notes by <a href="${AI_FEATURE_URL}">Fellow</a></p>`;
	const text = `Meeting: ${title} | AI Notes by Fellow\n\n`;

	return [html, text];
}
